import type { ReactElement } from "react"
import { useMemo, useState } from "react"
import { useQuery } from "urql"

import SearchSuggestion from "@/components/SearchSuggestion"
import { DEBOUNCE_DELAY_MS, FULL_LIST_LIMIT } from "@/constants/common"
import { ProductListSuggestionDocument } from "@/graphql/codegen/graphql"
import { ACTIVE_PRODUCT_STATUSES } from "@/screens/Products/constants"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface AssociateProductSearchSuggestionProps {
  showInactive: boolean
  onSearch: (value: string | AutocompleteOption) => void
  searchInputValue: string
  onInputValueChange: (value: string) => void
  excludedProductIds: number[]
}

export const AssociateProductSearchSuggestion = ({
  showInactive,
  onSearch,
  searchInputValue,
  onInputValueChange,
  excludedProductIds,
}: AssociateProductSearchSuggestionProps): ReactElement => {
  const [inputValue, setInputValue] = useState(searchInputValue)
  const debouncedQuery = useDebounce(inputValue, DEBOUNCE_DELAY_MS)

  const [{ data, fetching: isFetching }] = useQuery({
    query: ProductListSuggestionDocument,
    variables: {
      input: {
        filter: {
          query: debouncedQuery,
          ...(!showInactive && { computedProductStatuses: ACTIVE_PRODUCT_STATUSES }),
        },
        offset: 0,
        limit: FULL_LIST_LIMIT,
      },
    },
    pause: debouncedQuery.length < 2,
  })

  const options = useMemo<AutocompleteOption[]>(() => {
    if (isFetching) return []
    const products = data?.product?.list?.__typename === "ProductListSuccess" ? data.product.list.products : []
    return products
      .filter((product) => !excludedProductIds.includes(product.productId))
      .map((product) => ({
        label: product.companyProductDescription ?? `Product ${product.productId}`,
        id: product.productId,
      }))
  }, [data, isFetching, excludedProductIds])

  const handleSetInputValue = (value: string) => {
    setInputValue(value)
    onInputValueChange(value)
    if (!value) onSearch("")
  }

  return (
    <SearchSuggestion
      onSearch={onSearch}
      placeholder='Search by Product Name or Product ID'
      inputValue={inputValue}
      setInputValue={handleSetInputValue}
      options={options}
      fetching={isFetching}
    />
  )
}
